import React, { useState, useEffect } from 'react';
import { db } from '../../../../firebase';
import { collection, onSnapshot, query, orderBy, deleteDoc, doc, updateDoc } from 'firebase/firestore';
import { NoticeModel, MemberModel, NOTICE_CATEGORIES } from '../../../../models';
import { Plus, Search, Edit2, Trash2, Pin, Bell, Filter } from 'lucide-react';
import NoticeEditor from './NoticeEditor';
import AdminPageHeader from '../../frame/RightPanelHeader';
import Pagination from '../../../../components/ui/Pagination';

interface NoticeManagementProps {
    notices: NoticeModel[];
    currentAdmin: MemberModel | null;
    branches?: { id: string, name: string }[];
}

const NoticeManagement: React.FC<NoticeManagementProps> = ({ notices: initialNotices, currentAdmin, branches = [] }) => {
    const [isEditorOpen, setIsEditorOpen] = useState(false);
    const [editingNotice, setEditingNotice] = useState<NoticeModel | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [categoryFilter, setCategoryFilter] = useState<string>('전체');
    const [currentPage, setCurrentPage] = useState(1);
    const ITEMS_PER_PAGE = 10;

    // Filter pinned and sorted locally if needed, or just use the passed notices.
    // The ManagerRoot already sorts by createdAt desc. 
    // But NoticeManagement sorts by pinned first.
    const notices = initialNotices;



    const [categories, setCategories] = useState<string[]>(NOTICE_CATEGORIES as any);

    useEffect(() => {
        const fetchCategories = async () => {
            const settingSnap = await import('firebase/firestore').then(mod => mod.getDoc(mod.doc(db, 'settings', 'site')));
            if (settingSnap.exists()) {
                const data = settingSnap.data() as any;
                if (data.noticeCategories && data.noticeCategories.length > 0) {
                    setCategories(data.noticeCategories);
                } else {
                    setCategories(NOTICE_CATEGORIES as any);
                }
            }
        };
        fetchCategories();
    }, []);

    const handleDelete = async (id: string) => {
        if (!confirm('정말로 이 공지사항을 삭제하시겠습니까?')) return;
        try {
            await deleteDoc(doc(db, 'notices', id));
        } catch (error) {
            console.error('Error deleting notice:', error);
            alert('삭제 중 오류가 발생했습니다.');
        }
    };

    const togglePin = async (notice: NoticeModel) => {
        try {
            await updateDoc(doc(db, 'notices', notice.id), {
                isPinned: !notice.isPinned
            });
        } catch (error) {
            console.error('Error toggling pin:', error);
        }
    };

    const filteredNotices = notices
        .filter(n => {
            const matchesSearch = n.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                n.content.toLowerCase().includes(searchQuery.toLowerCase());
            const matchesCategory = categoryFilter === '전체' || n.category === categoryFilter;
            return matchesSearch && matchesCategory;
        })
        .sort((a, b) => {
            if (!a.isPinned && b.isPinned) return 1;
            return b.createdAt - a.createdAt;
        });

    const totalPages = Math.ceil(filteredNotices.length / ITEMS_PER_PAGE);
    const paginatedNotices = filteredNotices.slice(
        (currentPage - 1) * ITEMS_PER_PAGE,
        currentPage * ITEMS_PER_PAGE
    );

    useEffect(() => {
        setCurrentPage(1);
    }, [searchQuery, categoryFilter]);

    if (isEditorOpen) {
        return (
            <NoticeEditor
                notice={editingNotice}
                currentAdmin={currentAdmin}
                onClose={() => {
                    setIsEditorOpen(false);
                    setEditingNotice(null);
                }}
            />
        );
    }

    return (
        <div className="flex flex-col h-full bg-slate-50 overflow-hidden w-full animate-in fade-in duration-500">
            <AdminPageHeader
                title="공지사항 관리"
                icon={Bell}
                subtitle="학생 및 강사에게 전달할 공지사항을 관리합니다."
                actions={
                    <button
                        onClick={() => setIsEditorOpen(true)}
                        className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-2.5 rounded-full text-sm font-black flex items-center gap-2 shadow-lg shadow-indigo-100 transition-all active:scale-95"
                    >
                        <Plus size={18} strokeWidth={3} />
                        <span>새 공지 작성</span>
                    </button>
                }
            />

            {/* Search & Filter Area */}
            <div className="px-8 py-5 border-b border-slate-200 bg-white shrink-0 z-10 w-full">
                <div className="flex gap-3 w-full">
                    <div className="relative flex-1 group">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-indigo-500 transition-colors" size={18} />
                        <input
                            type="text"
                            placeholder="제목 또는 내용 검색..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full bg-slate-50 border border-slate-200 rounded-lg pl-12 pr-4 py-3 text-sm font-medium outline-none focus:ring-2 focus:ring-indigo-500 focus:bg-white transition-all shadow-sm"
                        />
                    </div>
                    <div className="relative">
                        <Filter className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                        <select
                            value={categoryFilter}
                            onChange={(e) => setCategoryFilter(e.target.value)}
                            className="bg-slate-50 border border-slate-200 rounded-lg pl-12 pr-10 py-3 text-sm font-bold outline-none focus:ring-2 focus:ring-indigo-500 appearance-none cursor-pointer min-w-[140px] shadow-sm"
                        >
                            <option value="전체">모든 카테고리</option>
                            {categories.map(cat => (
                                <option key={cat} value={cat}>{cat}</option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>

            {/* List */}
            <div className="flex-1 overflow-y-auto p-8">
                <div className="max-w-6xl mx-auto space-y-4">
                    {paginatedNotices.length > 0 ? (
                        <>
                            {paginatedNotices.map((notice) => (
                                <div
                                    key={notice.id}
                                    className={`bg-white rounded-xl p-6 shadow-sm border border-slate-100 hover:shadow-md transition-all group ${notice.isPinned ? 'ring-2 ring-indigo-500/20' : ''}`}
                                >
                                    <div className="flex items-start justify-between">
                                        <div className="flex-1">
                                            <div className="flex items-center space-x-3 mb-2">
                                                {notice.isPinned && (
                                                    <span className="bg-indigo-100 text-indigo-700 px-3 py-1 rounded-full text-xs font-bold flex items-center">
                                                        <Pin size={12} className="mr-1 fill-current" />
                                                        고정됨
                                                    </span>
                                                )}
                                                <span className={`px-3 py-1 rounded-full text-xs font-bold ${notice.category === '중요' ? 'bg-rose-100 text-rose-700' :
                                                    notice.category === '학습' ? 'bg-emerald-100 text-emerald-700' :
                                                        notice.category === '지점' ? 'bg-amber-100 text-amber-700' :
                                                            'bg-slate-100 text-slate-700'
                                                    }`}>
                                                    {notice.category}
                                                </span>
                                                <span className="text-slate-400 text-xs">
                                                    {new Date(notice.createdAt).toLocaleDateString()}
                                                </span>
                                                {/* Branch Badge for Super Admin */}
                                                {currentAdmin?.role === 'super_admin' && (
                                                    <span className="px-2 py-0.5 rounded-md bg-slate-50 text-slate-500 text-[10px] font-black uppercase border border-slate-100">
                                                        {(notice.branchId === 'all' || !notice.branchId)
                                                            ? '전체 지점'
                                                            : (branches.find(b => b.id === notice.branchId)?.name || '지점')}
                                                    </span>
                                                )}
                                            </div>
                                            <h3 className="text-xl font-bold text-slate-900 mb-2 truncate group-hover:text-indigo-600 transition-colors">
                                                {notice.title}
                                            </h3>
                                            <div className="text-slate-600 line-clamp-2 text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: notice.content.replace(/<[^>]*>/g, ' ').substring(0, 200) }} />
                                        </div>

                                        <div className="flex items-center space-x-2 ml-4">
                                            <button
                                                onClick={() => togglePin(notice)}
                                                className={`p-2.5 rounded-lg transition-all ${notice.isPinned ? 'text-indigo-600 bg-indigo-50' : 'text-slate-400 hover:bg-slate-50'}`}
                                                title={notice.isPinned ? "고정 해제" : "상단 고정"}
                                            >
                                                <Pin size={18} className={notice.isPinned ? "fill-current" : ""} />
                                            </button>
                                            <button
                                                onClick={() => {
                                                    setEditingNotice(notice);
                                                    setIsEditorOpen(true);
                                                }}
                                                className="p-2.5 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all"
                                                title="수정"
                                            >
                                                <Edit2 size={18} />
                                            </button>
                                            <button
                                                onClick={() => handleDelete(notice.id)}
                                                className="p-2.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-all"
                                                title="삭제"
                                            >
                                                <Trash2 size={18} />
                                            </button>
                                        </div>
                                    </div>
                                    <div className="mt-4 flex items-center space-x-4 text-xs text-slate-400">
                                        <span className="flex items-center">
                                            작성자: {notice.authorName}
                                        </span>
                                        <span>•</span>
                                        <span>조회수: {notice.viewCount}</span>
                                        <span>•</span>
                                        <span className="flex flex-wrap gap-2">
                                            대상: {notice.targetAudience.map(a =>
                                                a === 'student' ? '학생' :
                                                    a === 'teacher' ? '선생님' : a
                                            ).join(', ')}
                                        </span>
                                    </div>
                                </div>
                            ))}
                            <Pagination
                                currentPage={currentPage}
                                totalPages={totalPages}
                                onPageChange={setCurrentPage}
                            />
                        </>
                    ) : (
                        <div className="bg-white rounded-xl p-12 text-center border-2 border-dashed border-slate-200">
                            <div className="bg-slate-50 w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 text-slate-300">
                                <Bell size={32} />
                            </div>
                            <h3 className="text-lg font-bold text-slate-900 mb-2">공지사항이 없습니다</h3>
                            <p className="text-slate-500">새로운 공지사항을 작성하여 학생들에게 소식을 전해보세요.</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default NoticeManagement;
